// UD-559: the pieces of the Active Users page that are plain logic (no React), so they can be
// tested on their own.

export const NO_OPEN_CATEGORY = 'no_open_60_days'

export type ActiveUserCardValue =
	| 'all'
	| 'highly_active'
	| 'active'
	| 'potential'
	| 'at_risk'
	| 'dormant'
	| typeof NO_OPEN_CATEGORY

// The one place the card ranges come from. They mirror the server job that sorts every user into a
// group (userActivityWorkerService.processDailyUserActivity in vn-connection-be): the count of
// days with at least one session inside the rolling window, VIP from 20, Active from 10, Potential
// from 3, At Risk from 1 and Dormant at 0. The window holds 30 days (SUMMARY_WINDOW_DAYS on the
// server), so 30 is the most a person can have. Every upper bound below is the next group's lower
// bound minus one. The overview call does not send these numbers, so keep them in step with the
// server if it ever changes.
export const ACTIVE_DAYS_WINDOW = 30

const ACTIVE_DAYS_FROM = {
	highly_active: 20,
	active: 10,
	potential: 3,
	at_risk: 1,
	dormant: 0,
} as const

const ACTIVE_DAYS_ORDER = [
	'highly_active',
	'active',
	'potential',
	'at_risk',
	'dormant',
] as const

// "20-30 days": the lower and the upper number of active days a card covers, inclusive on both
// ends. "All" is the full range, Dormant is exactly 0 and the 60 day card has no range.
export const activeDaysRange = (
	card: ActiveUserCardValue,
): { from: number; to: number } | null => {
	if (card === 'all') return { from: 0, to: ACTIVE_DAYS_WINDOW }
	const index = ACTIVE_DAYS_ORDER.indexOf(card as (typeof ACTIVE_DAYS_ORDER)[number])
	if (index < 0) return null
	const from = ACTIVE_DAYS_FROM[ACTIVE_DAYS_ORDER[index]]
	const to =
		index === 0
			? ACTIVE_DAYS_WINDOW
			: ACTIVE_DAYS_FROM[ACTIVE_DAYS_ORDER[index - 1]] - 1
	return { from, to }
}

export const activeDaysLabel = (card: ActiveUserCardValue): string => {
	const range = activeDaysRange(card)
	if (!range) return ''
	return range.from === range.to
		? `${range.from} ${range.from === 1 ? 'day' : 'days'}`
		: `${range.from}-${range.to} days`
}

// The line under the title of every card (UD-559, the cards must add up). It says what the card
// counts. The four active groups say how many days of the last 30 they cover. "All" is everyone
// who opened the app in the last 60 days (the five groups added up), Dormant is the group that did
// not open the app in the last 30 days but did 31 to 60 days ago, and the 60 day card is everyone
// else who is not deleted. Together: All + No app open in 60 days = Total users.
export const ALL_CARD_TEXT = 'Opened the app in the last 60 days'
export const DORMANT_CARD_TEXT =
	'No open in the last 30 days, last open 31 to 60 days ago'
export const NO_OPEN_CARD_TEXT = 'Not deleted, no session in 60 days'
export const CARDS_ADD_UP_TEXT = 'All + No app open in 60 days = Total users'

export const cardDescription = (card: ActiveUserCardValue): string => {
	if (card === 'all') return ALL_CARD_TEXT
	if (card === 'dormant') return DORMANT_CARD_TEXT
	if (card === NO_OPEN_CATEGORY) return NO_OPEN_CARD_TEXT
	return activeDaysLabel(card)
}

export interface ActiveUsersOverview {
	window_start?: string | null
	window_end?: string | null
	summary_updated_at?: string | null
	total_users?: number | string | null
	summary_total?: number | string | null
	categories?: Record<string, number | string | null> | null
	no_open?: {
		days?: number
		window_start?: string | null
		window_end?: string | null
		people?: number | string | null
	} | null
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

const parseDateOnly = (value?: string | null) => {
	const match = DATE_ONLY.exec(String(value || ''))
	if (!match) return null
	return { year: match[1], month: match[2], day: match[3] }
}

// "08/09 - 07/10/2026": the rolling last 30 days as the server counted them. The year is printed
// once when both ends are in the same year, on both ends when the window crosses a year end.
// An old server sends no dates, and then nothing is shown.
export const formatWindowRange = (
	start?: string | null,
	end?: string | null,
): string => {
	const from = parseDateOnly(start)
	const to = parseDateOnly(end)
	if (!from || !to) return ''
	if (from.year === to.year) {
		return `${from.day}/${from.month} - ${to.day}/${to.month}/${to.year}`
	}
	return `${from.day}/${from.month}/${from.year} - ${to.day}/${to.month}/${to.year}`
}

// A number as the page prints every count: 41355 -> "41,355". Anything that is not a number
// (a missing overview, an old server) prints nothing, so a card never shows a false zero.
export const formatCount = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return ''
	const count = Number(value)
	if (!Number.isFinite(count)) return ''
	return count.toLocaleString('en-US')
}

// The number on a card. "All" is every row the list holds (the five groups added up), the five
// groups are their own counts, and the 60 day card is the no-open count.
export const cardCount = (
	overview: ActiveUsersOverview | null | undefined,
	card: ActiveUserCardValue,
): number | null => {
	if (!overview) return null
	if (card === 'all') {
		const total = Number(overview.summary_total)
		return Number.isFinite(total) && overview.summary_total !== null && overview.summary_total !== undefined
			? total
			: null
	}
	if (card === NO_OPEN_CATEGORY) {
		const people = Number(overview.no_open?.people)
		return Number.isFinite(people) && overview.no_open?.people !== null && overview.no_open?.people !== undefined
			? people
			: null
	}
	const value = overview.categories?.[card]
	if (value === null || value === undefined) return null
	const count = Number(value)
	return Number.isFinite(count) ? count : null
}

// A deleted account: its user row says so, or the user could not be read at all.
export const isDeletedActiveUser = (row?: {
	user?: { deleted_at?: string | null } | null
} | null): boolean => {
	if (!row) return false
	return !row.user || Boolean(row.user.deleted_at)
}

export const exportParams = (
	card: ActiveUserCardValue,
	searchField?: string | null,
	searchValue?: string | null,
) => {
	const value = (searchValue || '').trim()
	return {
		...(card !== 'all' ? { category: card } : {}),
		...(value && searchField
			? { search_field: searchField, search_value: value }
			: {}),
	}
}

export const exportFileName = (card: ActiveUserCardValue, now = new Date()) => {
	const pad = (value: number) => String(value).padStart(2, '0')
	const day = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
	return `active-users-${card}-${day}.csv`
}

// The file name the server chose, or ours when the header is missing or unreadable.
export const fileNameFromDisposition = (
	header: string | undefined,
	fallback: string,
) => {
	const match = /filename="?([^";]+)"?/i.exec(header || '')
	const name = match?.[1]?.trim()
	return name && /^[\w.-]+$/.test(name) ? name : fallback
}

// With responseType blob an error body arrives as a Blob. Read the server message out of it so the
// existing error dialog can show it.
export const readBlobError = async (error: any) => {
	const data = error?.response?.data
	if (data && typeof data.text === 'function') {
		try {
			const parsed = JSON.parse(await data.text())
			return { message: error?.message, response: { data: parsed } }
		} catch {
			return error
		}
	}
	return error
}

// Hands a downloaded Blob to the browser as a file. The BOM the server writes stays in the bytes.
export const saveBlobAs = (blob: Blob, fileName: string) => {
	const url = window.URL.createObjectURL(
		new Blob([blob], { type: 'text/csv;charset=utf-8' }),
	)
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}
